/**
 * Field — label + input + optional hint / error. The base form primitive.
 * References semantic tokens only via {@link useTheme}.
 */

import React from 'react';
import { useTheme } from '../theme/index.js';

export interface FieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style' | 'size'> {
  /**
   * Visible label above the input. Omit it in tabular layouts where the column
   * header labels the field once — then pass {@link ariaLabel} so the control
   * stays accessible.
   */
  readonly label?: string | undefined;
  /** Accessible name used when {@link label} is omitted (e.g. a table column). */
  readonly ariaLabel?: string;
  /** Helper text shown below the input when there is no error. */
  readonly hint?: string | undefined;
  /**
   * Error message; when present the field renders in the danger state.
   * Accepts `undefined` so callers (e.g. react-hook-form `errors.x?.message`)
   * can pass an optional error directly under `exactOptionalPropertyTypes`.
   */
  readonly error?: string | undefined;
  /** Use the larger planta field height (56px) for gloved use. */
  readonly planta?: boolean;
  /**
   * Show a reveal (show/hide) toggle for a password input. Only takes effect when
   * `type="password"`; clicking it swaps the input between `password` and `text`.
   * Labels are passed in (already translated) so the design system stays i18n-free.
   */
  readonly revealable?: boolean;
  /** Accessible label for the reveal toggle while the password is hidden. */
  readonly showPasswordLabel?: string;
  /** Accessible label for the reveal toggle while the password is shown. */
  readonly hidePasswordLabel?: string;
  readonly containerStyle?: React.CSSProperties | undefined;
  readonly inputStyle?: React.CSSProperties | undefined;
}

/** Eye / eye-off glyph for the reveal toggle. */
function eyeIcon(shown: boolean, px: number): React.ReactElement {
  return React.createElement(
    'svg',
    {
      width: px,
      height: px,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': true,
      focusable: false,
    },
    React.createElement('path', {
      key: 'p',
      d: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z',
    }),
    React.createElement('circle', { key: 'c', cx: 12, cy: 12, r: 3 }),
    shown ? React.createElement('line', { key: 'l', x1: 3, y1: 3, x2: 21, y2: 21 }) : null,
  );
}

// forwardRef so react-hook-form's `{...register(name)}` ref reaches the <input>.
// Without it RHF can't read the value and required fields fail validation even
// when filled.
export const Field = React.forwardRef<HTMLInputElement, FieldProps>(function Field(
  props,
  ref,
): React.ReactElement {
  const {
    label,
    ariaLabel,
    hint,
    error,
    planta = false,
    revealable = false,
    showPasswordLabel,
    hidePasswordLabel,
    containerStyle,
    inputStyle,
    id,
    disabled = false,
    onFocus,
    onBlur,
    ...rest
  } = props;

  const theme = useTheme();
  const [focused, setFocused] = React.useState(false);
  const [revealed, setRevealed] = React.useState(false);
  // The reveal toggle only applies to password inputs; it swaps the input type.
  const canReveal = revealable && rest.type === 'password';
  const effectiveType = canReveal ? (revealed ? 'text' : 'password') : rest.type;
  const reactId = React.useId();
  const fieldId = id ?? reactId;
  const describedById = `${fieldId}-desc`;

  const hasError = Boolean(error);
  const height = planta ? theme.spacing.size.fieldHeightPlanta : theme.spacing.size.fieldHeight;

  const borderColor = hasError
    ? theme.color.danger
    : focused
      ? theme.color.borderFocus
      : theme.color.border;

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
    fontWeight: theme.typography.weight.semibold,
    color: theme.color.textPrimary,
    marginBottom: theme.spacing.space[3],
  };

  const composedInputStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    height,
    paddingInline: theme.spacing.space[4],
    backgroundColor: disabled ? theme.color.surfaceSunken : theme.color.surface,
    color: disabled ? theme.color.textDisabled : theme.color.textPrimary,
    border: `1px solid ${borderColor}`,
    borderRadius: theme.radii.radius.field,
    fontFamily: theme.typography.family.sans,
    fontSize: planta ? theme.typography.size.bodyLg : theme.typography.size.body,
    lineHeight: theme.typography.lineHeight.normal,
    outline: 'none',
    boxShadow: focused && !hasError ? theme.radii.shadow.focusRing : 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    // Reserve room on the trailing edge for the reveal toggle so text never
    // slides under it.
    ...(canReveal ? { paddingInlineEnd: theme.spacing.size.touchTargetMin } : null),
    ...inputStyle,
  };

  const messageStyle: React.CSSProperties = {
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.caption,
    color: hasError ? theme.color.dangerText : theme.color.textTertiary,
    marginTop: theme.spacing.space[2],
  };

  const message = error ?? hint;

  return React.createElement(
    'div',
    {
      style: { display: 'flex', flexDirection: 'column', ...containerStyle },
    },
    label
      ? React.createElement('label', { htmlFor: fieldId, style: labelStyle }, label)
      : null,
    React.createElement(
      'div',
      { style: { position: 'relative', display: 'block' } },
      React.createElement('input', {
        ...rest,
        type: effectiveType,
        ref,
        id: fieldId,
        disabled,
        'aria-label': label ? undefined : ariaLabel,
        'aria-invalid': hasError,
        'aria-describedby': message ? describedById : undefined,
        style: composedInputStyle,
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          setFocused(true);
          onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          setFocused(false);
          onBlur?.(e);
        },
      }),
      canReveal
        ? React.createElement(
            'button',
            {
              type: 'button',
              tabIndex: -1,
              disabled,
              'aria-label': revealed ? hidePasswordLabel : showPasswordLabel,
              'aria-pressed': revealed,
              onClick: () => setRevealed((v) => !v),
              style: {
                position: 'absolute',
                insetInlineEnd: 0,
                top: 0,
                height: '100%',
                width: theme.spacing.size.touchTargetMin,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                background: 'transparent',
                border: 'none',
                color: theme.color.textTertiary,
                cursor: disabled ? 'default' : 'pointer',
              },
            },
            eyeIcon(revealed, theme.spacing.size.iconMd),
          )
        : null,
    ),
    message
      ? React.createElement('span', { id: describedById, style: messageStyle }, message)
      : null,
  );
});
